import { useQuery, useInfiniteQuery, useQueryClient, InfiniteData } from '@tanstack/react-query';
import { AICreation, AICreationType, listAiCreations, ListAiCreationsResponse } from '@/api/list-ai-creations';
import { useCallback, useMemo, useEffect, useState } from 'react';
import {
  AssetCreationToolName,
  CompletedStandaloneMediaAsset,
  MediaAssetStatus,
  StandaloneMediaAsset,
} from '@/types/media';
import { StudioVideo, StudioVideoStatus } from '@/features/ai-video-creator/stores/studioVideoStore';
import { useLibraryNotificationsStore } from '@/features/notifications/stores/libraryNotificationsStore';
import { useCurrentApp } from '@/stores/userDataStore';
import { useShallow } from 'zustand/react/shallow';
import { LibraryNotification } from '@/features/notifications/stores/libraryNotificationsStore';
import { TemplateName } from '@/features/studio-video-templates/types/template';

export const TEMPLATES_SHOWING_MEDIA_ASSETS = [
  TemplateName.Lookbook,
  TemplateName.SizingVideos,
  TemplateName.ProductRotate,
];

type UseAllAiCreationsProps = {
  appKey: string;
};

export const useAllAiCreations = ({ appKey }: UseAllAiCreationsProps) => {
  const { setNotifications } = useLibraryNotificationsStore(useShallow((state) => state));
  const { isLoggedIn, isLoading: isLoadingUser } = useCurrentApp();
  const { data, isLoading, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: ['ai-creations', appKey],
    queryFn: ({ pageParam = 0 }) => listAiCreations({ appKey, cursor: pageParam, size: 50 }),
    enabled: !!appKey && isLoggedIn,
    getNextPageParam: (lastPage, pages) => lastPage.nextCursor,
    initialPageParam: 0,
  });

  const { aiCreations, failedCreations } = useMemo(() => {
    const onlySuccessfulCreations: AICreation[] = [];
    const failedCreations: AICreation[] = [];

    if (data?.pages) {
      data.pages.forEach((group) => {
        group.items.forEach((creation) => {
          const { item, type } = creation;

          if (
            type === AICreationType.mediaAsset &&
            item.createdWith.toolName === AssetCreationToolName.studioVideoComposition &&
            !(
              item.metadata?.sourceTemplate &&
              TEMPLATES_SHOWING_MEDIA_ASSETS.includes(item.metadata.sourceTemplate as TemplateName)
            )
          ) {
            return;
          }

          const isFailed = item.status === MediaAssetStatus.FAILED;
          if (isFailed) {
            failedCreations.push(creation);
          } else {
            onlySuccessfulCreations.push(creation);
          }
        });
      });
    }
    return { aiCreations: onlySuccessfulCreations, failedCreations };
  }, [data, setNotifications]);

  useEffect(() => {
    if (!failedCreations) return;
    const notifications: LibraryNotification[] = [];

    failedCreations.forEach((creation) => {
      const { item, type } = creation;
      if (type === AICreationType.mediaAsset) {
        notifications.push({
          id: creation.id,
          source: 'media_asset',
          title: 'Failed to generate Media Asset',
          description: item.name,
          tone: 'critical',
          toolUsed: item.createdWith.toolName,
          generatedAt: item.createdAt,
          error: item.error,
        });
      } else if (type === AICreationType.studioVideo) {
        notifications.push({
          id: creation.id,
          source: 'studio_video',
          title: 'Failed to generate Studio Video',
          description: item.name || 'Ai Video',
          tone: 'critical',
          toolUsed: 'studio_video',
          generatedAt: item.createdAt || new Date().toISOString(),
          error: item.error,
        });
      }
    });

    setNotifications(notifications);
  }, [failedCreations, setNotifications]);

  return {
    aiCreations,
    isLoading: isLoadingUser || isLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  };
};

export const useAllAiCreationsCache = ({ appKey }: UseAllAiCreationsProps) => {
  const queryClient = useQueryClient();

  const getItemId = useCallback((item: { id?: string }, type: AICreationType) => {
    return `${type}#${item.id || 'null_id'}`;
  }, []);

  const updateCacheData = useCallback(
    (type: AICreationType, newItems: AICreation['item'][]) => {
      const itemsMap = newItems.reduce((map, item) => {
        return map.set(getItemId(item, type), item);
      }, new Map<string, AICreation['item']>());

      queryClient.setQueryData<InfiniteData<ListAiCreationsResponse>>(['ai-creations', appKey], (oldData) => {
        if (!oldData) return oldData;

        const updatedPages = oldData.pages.map((page) => {
          const updatedItems = page.items.map((oldCreation) => {
            if (oldCreation.type !== type) {
              return oldCreation;
            }

            const newItem = itemsMap.get(getItemId(oldCreation.item, type));
            if (!newItem) {
              return oldCreation;
            }

            const newCreation = { ...oldCreation };
            newCreation.item = newItem;
            return newCreation;
          });

          return { ...page, items: updatedItems };
        });

        return { ...oldData, pages: updatedPages };
      });
    },
    [queryClient, appKey, getItemId]
  );

  const updateMediaAssetsCache = useCallback(
    (mediaAssets: CompletedStandaloneMediaAsset[]) => {
      updateCacheData(AICreationType.mediaAsset, mediaAssets);
    },
    [updateCacheData]
  );

  const updateStudioVideosCache = useCallback(
    (studioVideos: StudioVideo[]) => {
      updateCacheData(AICreationType.studioVideo, studioVideos);
    },
    [updateCacheData]
  );

  const invalidateAllAiCreationsQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['ai-creations'] });
  }, [queryClient]);

  return {
    updateMediaAssetsCache,
    updateStudioVideosCache,
    invalidateAllAiCreationsQuery,
  };
};

export const useAllAiCreationsPrefetch = () => {
  const { appKey, isLoggedIn, isLoading: isLoadingUser } = useCurrentApp();
  const queryClient = useQueryClient();
  const prefetchAllAiCreations = useCallback(() => {
    if (appKey && isLoggedIn) {
      queryClient.prefetchQuery({
        queryKey: ['ai-creations', appKey],
        queryFn: () => listAiCreations({ appKey }),
        staleTime: 5 * 60 * 1000,
      });
    }
  }, [appKey, isLoggedIn, queryClient]);

  useEffect(() => {
    prefetchAllAiCreations();
  }, [prefetchAllAiCreations]);

  return prefetchAllAiCreations;
};
